import { useAppSelector } from '@/hooks'
import {
  DeviceIneligibilityMessages,
  programsNewCollection,
  selectAllDeviceIneligibilityMessages,
  selectAllProgramEnrolledDevices,
  selectAvailablePrograms,
  selectEnrolledPrograms,
} from '@/reducers/programsNew'
import { ProgramsNew } from '@/types/programs-new/programs-new'
import { VehicleEligibilityRequirement } from '@/types/programs-new/vehicle-requirements'
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { match } from 'ts-pattern'
import {
  DeviceType,
  DeviceUrlSlug,
} from '../components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import getProgramEnrollmentExpiration from '@/utils/utility-program-helpers/getProgramEnrollmentExpiration'
import {
  getDeviceVerificationStatus,
  VerificationStatusTagType,
} from '@/hooks/useDeviceVerificationStatus/useDeviceVerificationStatus'
import { deviceTypeFromUrlSlug } from '../components/DeviceSelectionGallery/DeviceSelectionGallery.utils'

export default function useUtilityPrograms({
  onlyMultiPrograms,
}: {
  onlyMultiPrograms: boolean
}) {
  const { deviceId, deviceType } = useParams() as {
    deviceId?: string
    deviceType?: DeviceUrlSlug
  }
  const selectedDeviceId = useMemo(
    () => (deviceId ? +deviceId : -1),
    [deviceId],
  )
  const location = useLocation()
  const { isLoadingOrIdle } = programsNewCollection.useFetch()
  const enrolledPrograms = useAppSelector(
    selectEnrolledPrograms(onlyMultiPrograms),
  )

  const availablePrograms = useAppSelector(
    selectAvailablePrograms(onlyMultiPrograms),
  )
  const allDeviceIneligibilityMessages = useAppSelector(
    selectAllDeviceIneligibilityMessages(),
  )
  const allProgramEnrolledDevices = useAppSelector(
    selectAllProgramEnrolledDevices(),
  )

  const allLegacyPrograms = [
    ...useAppSelector(selectEnrolledPrograms(false)),
    ...useAppSelector(selectAvailablePrograms(false)),
  ].filter((p) => !p?.view_config_json?.useMultiProgramFlow)

  // programs the device can technically be added to (high confidence)
  // but needs to be re-enrolled currently, this is used to show ProgramCard disclaimer
  const enrolledProgramsSelectedDeviceCanBeAddedTo = useMemo(
    () =>
      enrolledPrograms.filter((p) => {
        const programMessages = allDeviceIneligibilityMessages.find(
          (m) => m.utilityProgramId === p.id,
        )
        const vehicleCanBeAdded =
          deviceType === 'vehicle' &&
          !p.enrollment_overview?.vehicle_enrollments.some(
            (e) => e.vehicle.id === selectedDeviceId,
          )
        const thermostatCanBeAdded =
          deviceType === 'thermostat' &&
          !p.enrollment_overview?.thermostat_enrollments.some(
            (e) => e.thermostat.id === selectedDeviceId,
          )
        const chargerCanBeAdded =
          deviceType === 'vehicle-charger' &&
          !p.enrollment_overview?.charger_enrollments.some(
            (e) => e.charger.id === selectedDeviceId,
          )
        const isDeviceAllowed = isDeviceAllowedInProgram(
          selectedDeviceId,
          deviceType ? deviceTypeFromUrlSlug(deviceType) : 'home',
          p.device_eligibility_overview,
          programMessages?.messages,
        )
        return (
          (vehicleCanBeAdded || thermostatCanBeAdded || chargerCanBeAdded) &&
          isDeviceAllowed
        )
      }),
    [availablePrograms, enrolledPrograms, selectedDeviceId, location.pathname],
  )

  // these device program variables are meant to be temp until we
  // implement a dedicated programs tab that does not have a
  // selected device id associated with it
  const availableDevicePrograms = useMemo(() => {
    const available = availablePrograms.filter((p) => {
      const programMessages = allDeviceIneligibilityMessages.find(
        (m) => m.utilityProgramId === p.id,
      )
      // need to remove devices from the available list that we know are not allowed
      return isDeviceAllowedInProgram(
        selectedDeviceId,
        deviceType ? deviceTypeFromUrlSlug(deviceType) : 'home',
        p.device_eligibility_overview,
        programMessages?.messages,
      )
    })

    // add enrolled programs where the user can add a device to the available section
    const enrolledWithOtherDevice = enrolledPrograms
      .filter((p) =>
        enrolledProgramsSelectedDeviceCanBeAddedTo.find((ep) => ep.id === p.id),
      )
      .filter((p) => {
        // ... and the program is not yet expired
        const { isExpired } = getProgramEnrollmentExpiration(
          p.preferred_display_timezone,
          p.view_config_json?.programCard?.enrollmentEndDate,
          p.view_config_json?.programCard?.rollingDeadline ?? false,
        )
        return !isExpired
      })
    return [...available, ...enrolledWithOtherDevice]
  }, [
    selectedDeviceId,
    availablePrograms,
    allDeviceIneligibilityMessages,
    enrolledProgramsSelectedDeviceCanBeAddedTo,
  ])

  const ineligibleDevicePrograms = useMemo(() => {
    return [...availablePrograms, ...enrolledPrograms].filter(
      (p) =>
        getDeviceVerificationStatus(
          p.device_eligibility_overview,
          p.enrollment_overview,
          p.active,
          selectedDeviceId,
          deviceTypeFromUrlSlug(deviceType ?? 'home'),
        )?.deviceStatus === VerificationStatusTagType.INELIGIBLE,
    )
  }, [selectedDeviceId, availablePrograms, enrolledPrograms])

  const pendingDevicePrograms = useMemo(() => {
    return enrolledPrograms.filter(
      (p) =>
        getDeviceVerificationStatus(
          p.device_eligibility_overview,
          p.enrollment_overview,
          p.active,
          selectedDeviceId,
          deviceTypeFromUrlSlug(deviceType ?? 'home'),
        )?.deviceStatus === VerificationStatusTagType.PENDING,
    )
  }, [enrolledPrograms])

  const enrolledDevicePrograms = useMemo(() => {
    const property = match(deviceType)
      .with('vehicle', () => 'vehicles' as const)
      .with('thermostat', () => 'thermostats' as const)
      .with('vehicle-charger', () => 'chargers' as const)
      .otherwise(() => null)

    if (!property) return []
    return enrolledPrograms
      .filter((program) =>
        allProgramEnrolledDevices
          .find((enrollment) => enrollment.utilityProgramId === program.id)
          ?.devices[property].find((device) => device.id === selectedDeviceId),
      )
      .filter(
        (p) =>
          // remove enrolled programs where the user can add a device from the enrolled section (moves to available)
          !enrolledProgramsSelectedDeviceCanBeAddedTo.find(
            (ep) => ep.id === p.id,
          ),
      )
  }, [
    allProgramEnrolledDevices,
    enrolledPrograms,
    selectedDeviceId,
    enrolledProgramsSelectedDeviceCanBeAddedTo,
  ])

  const selectedTeslaIsMissingPermissions = useMemo(
    () =>
      Boolean(
        allDeviceIneligibilityMessages
          .flatMap((m) => m.messages)
          .flatMap((m) => m.vehicles)
          .filter(
            (m) =>
              m.vehicle.id === selectedDeviceId &&
              m.vehicle?.car?.car_model?.make.toLowerCase() === 'tesla',
          )
          .flatMap((m) => m.requirements)
          .filter((m) => !m.isEligible)
          .find(
            (m) =>
              m.key === VehicleEligibilityRequirement.CONTROLLABLE ||
              m.key === VehicleEligibilityRequirement.HAS_READ_PERMISSIONS,
          ),
      ),
    [allDeviceIneligibilityMessages, selectedDeviceId],
  )

  return {
    isLoading: isLoadingOrIdle,
    enrolledPrograms,
    enrolledDevicePrograms,
    availablePrograms,
    availableDevicePrograms,
    allDeviceIneligibilityMessages,
    enrolledProgramsSelectedDeviceCanBeAddedTo,
    selectedTeslaIsMissingPermissions,
    allLegacyPrograms,
    pendingPrograms: pendingDevicePrograms,
    ineligiblePrograms: ineligibleDevicePrograms,
  }
}

export const isDeviceAllowedInProgram = (
  deviceId: number | string,
  deviceType: DeviceType,
  deviceEligibility: ProgramsNew['device_eligibility_overview'],
  programMessages?: DeviceIneligibilityMessages,
) => {
  if (!programMessages) return false

  return match(deviceType)
    .with('vehicle', () => {
      const vehicleMessages = programMessages.vehicles
        .filter((v) => v.id === deviceId)
        .flatMap((v) => v.requirements)
        .filter((r) => !r.isEligible)
        .map((r) => r.key)
      const isInvalidVehicleMake = Boolean(
        vehicleMessages.find(
          (k) =>
            k === VehicleEligibilityRequirement.VEHICLE_MAKE ||
            k === VehicleEligibilityRequirement.NOT_ALLOWED,
        ),
      )
      return (
        !isInvalidVehicleMake &&
        (deviceEligibility.devices.vehicles?.some((v) => v.id === deviceId) ??
          false)
      )
    })
    .with(
      'thermostat',
      () =>
        deviceEligibility.devices.thermostats?.some((t) => t.id === deviceId) ??
        false,
    )
    .with(
      'vehicleCharger',
      () =>
        deviceEligibility.devices.chargers?.some((c) => c.id === deviceId) ??
        false,
    )
    .with(
      'energySystem',
      () =>
        deviceEligibility.devices.energy_systems?.some(
          (e) => e.id === deviceId,
        ) ?? false,
    )
    .with('home', () => true)
    .otherwise(() => false)
}
